import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as svc from './clientes-service';
import { clientesKeys } from './keys';
import { serviciosKeys } from '../../servicios/api/keys';
import { cobrosKeys } from '../../cobros/api/keys';
import { dashboardKeys } from '../../dashboard/api/keys';
import { cobradorKeys } from '../../cobrador/api/keys';
import type {
  CreateClienteDto,
  UpdateClienteDto,
  QueryClientesDto,
  QueryEstadoCuentaDto,
  EstadoCuentaExportFormat,
  EstadoCuentaExportFilters,
  QueryEstadoCuentaSerieMensualDto,
  QueryEstadoCuentaSimpleDto,
  EstadoCuentaSimpleExportFilters,
  PreviewClientDeactivationDto,
  ConfirmClientDeactivationDto,
  CambioDiaFacturacionDto,
} from './clientes-service';

function invalidateClientDeactivationData(
  queryClient: ReturnType<typeof useQueryClient>,
  idCliente: number,
): void {
  void queryClient.invalidateQueries({ queryKey: clientesKeys.detail(idCliente) });
  void queryClient.invalidateQueries({ queryKey: clientesKeys.lists() });
  void queryClient.invalidateQueries({ queryKey: clientesKeys.cobros(idCliente) });
  void queryClient.invalidateQueries({ queryKey: clientesKeys.estadoCuenta(idCliente) });
  void queryClient.invalidateQueries({ queryKey: clientesKeys.serieMensualBase(idCliente) });
  // Deactivation confirmations emit BAJA / REACTIVACION cliente_evento rows;
  // the Info-tab Eventos section must refresh with them.
  void queryClient.invalidateQueries({ queryKey: clientesKeys.eventos(idCliente) });
  void queryClient.invalidateQueries({ queryKey: serviciosKeys.all });
  void queryClient.invalidateQueries({ queryKey: cobrosKeys.all });
  void queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
  void queryClient.invalidateQueries({ queryKey: cobradorKeys.all });
}

export function useClientesList(params: QueryClientesDto) {
  return useQuery({
    // SAFETY: Query DTOs are JSON-compatible records; the key helper only needs this structural view.
    queryKey: clientesKeys.list(params as unknown as Record<string, unknown>),
    queryFn: () => svc.getClientes(params),
    staleTime: 30_000,
  });
}

interface UseClienteOptions {
  enabled?: boolean;
}

export function useCliente(id: number, options?: UseClienteOptions) {
  return useQuery({
    queryKey: clientesKeys.detail(id),
    queryFn: () => svc.getCliente(id),
    enabled: id > 0 && (options?.enabled ?? true),
    staleTime: 30_000,
  });
}

export function usePreviewClienteUbicacion(
  id: number,
  location: { latitud: number; longitud: number } | null,
) {
  return useQuery({
    queryKey:
      location === null
        ? [...clientesKeys.all, 'ubicacion-preview', id, null]
        : clientesKeys.ubicacionPreview(id, location),
    queryFn: () => {
      if (location === null) throw new Error('Client location preview requires GPS coordinates');
      return svc.previewClienteUbicacion(id, location);
    },
    enabled: id > 0 && location !== null,
    staleTime: 0,
    retry: false,
  });
}

export function useSyncClienteUbicacionFromPago() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (idPago: number) => svc.syncClienteUbicacionFromPago(idPago),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: clientesKeys.detail(data.idCliente) });
      void qc.invalidateQueries({ queryKey: clientesKeys.lists() });
      void qc.invalidateQueries({ queryKey: serviciosKeys.all });
      void qc.invalidateQueries({ queryKey: cobradorKeys.all });
    },
  });
}

export function useCreateCliente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateClienteDto) => svc.createCliente(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clientesKeys.lists() });
    },
  });
}

export function useUpdateCliente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdateClienteDto }) => svc.updateCliente(id, dto),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: clientesKeys.detail(variables.id) });
      qc.invalidateQueries({ queryKey: clientesKeys.lists() });
    },
  });
}

/**
 * Server-computed preview of the optional adjustment when the billing date
 * moves. Disabled until the form carries a full candidate date, so opening
 * the form never fires a request.
 */
export function useCambioDiaFacturacionPreview(id: number, fechaCobro: string | null) {
  return useQuery({
    queryKey: clientesKeys.cambioDiaPreview(id, fechaCobro ?? ''),
    queryFn: () => svc.previewCambioDiaFacturacion(id, { fechaCobro: fechaCobro! }),
    enabled: id > 0 && fechaCobro !== null && fechaCobro.length === 10,
    staleTime: 0,
    retry: false,
  });
}

export function useCambioDiaFacturacion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: CambioDiaFacturacionDto }) =>
      svc.cambioDiaFacturacion(id, dto),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: clientesKeys.detail(variables.id) });
      void qc.invalidateQueries({ queryKey: clientesKeys.lists() });
      void qc.invalidateQueries({ queryKey: clientesKeys.cobros(variables.id) });
      void qc.invalidateQueries({ queryKey: clientesKeys.estadoCuenta(variables.id) });
      void qc.invalidateQueries({ queryKey: clientesKeys.serieMensualBase(variables.id) });
      void qc.invalidateQueries({ queryKey: cobrosKeys.all });
      void qc.invalidateQueries({ queryKey: dashboardKeys.all });
    },
  });
}

export function useDeleteCliente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => svc.deleteCliente(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clientesKeys.lists() });
    },
  });
}

interface UseClientDeactivationPreviewOptions {
  enabled?: boolean;
}

export function useClientDeactivationPreview(
  id: number,
  input: PreviewClientDeactivationDto,
  options?: UseClientDeactivationPreviewOptions,
) {
  return useQuery({
    queryKey: clientesKeys.deactivationPreview(id, input),
    queryFn: () => svc.previewClientDeactivation(id, input),
    enabled:
      id > 0 &&
      input.operationKey.length > 0 &&
      input.reason.trim().length > 0 &&
      (options?.enabled ?? true),
    staleTime: 0,
    retry: false,
  });
}

export function useClientDeactivation(id: number) {
  return useQuery({
    queryKey: clientesKeys.deactivation(id),
    queryFn: () => svc.getClientDeactivation(id),
    enabled: id > 0,
    staleTime: 30_000,
    retry: false,
  });
}

export function useConfirmClientDeactivation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: ConfirmClientDeactivationDto }) =>
      svc.confirmClientDeactivation(id, dto),
    onSuccess: (_data, variables) => {
      invalidateClientDeactivationData(qc, variables.id);
      void qc.invalidateQueries({ queryKey: clientesKeys.deactivation(variables.id) });
    },
  });
}

interface ClientQueryOptions {
  enabled?: boolean;
}

export function useClienteServicios(
  id: number,
  params: { limit?: number; offset?: number; estado?: number; incluirBajas?: boolean },
  options?: ClientQueryOptions,
) {
  return useQuery({
    queryKey: [...serviciosKeys.porCliente(id), params] as const,
    queryFn: () => svc.getClienteServicios(id, params),
    enabled: id > 0 && (options?.enabled ?? true),
    staleTime: 30_000,
  });
}

export function useClienteResumen(id: number, options?: ClientQueryOptions) {
  return useQuery({
    queryKey: [...clientesKeys.detail(id), 'resumen'] as const,
    queryFn: () => svc.getClienteResumen(id),
    enabled: id > 0 && (options?.enabled ?? true),
    staleTime: 30_000,
    retry: false,
  });
}

export function useClienteEventos(id: number) {
  return useQuery({
    queryKey: clientesKeys.eventos(id),
    queryFn: () => svc.getClienteEventos(id),
    enabled: id > 0,
    staleTime: 30_000,
    retry: false,
  });
}

export function useClienteCobros(id: number, params: { limit?: number; offset?: number }) {
  return useQuery({
    queryKey: [...clientesKeys.cobros(id), params] as const,
    queryFn: () => svc.getClienteCobros(id, params),
    enabled: id > 0,
    staleTime: 30_000,
  });
}

export function useClienteVisitas(id: number, params: { limit?: number; offset?: number }) {
  return useQuery({
    queryKey: [...clientesKeys.detail(id), 'visitas', params] as const,
    queryFn: () => svc.getClienteVisitas(id, params),
    enabled: id > 0,
    staleTime: 30_000,
  });
}

export function useClienteEstadoCuenta(id: number, params: QueryEstadoCuentaDto) {
  return useQuery({
    // SAFETY: Query DTOs are JSON-compatible records; the key helper only needs this structural view.
    queryKey: clientesKeys.estadoCuenta(id, params as unknown as Record<string, unknown>),
    queryFn: () => svc.getClienteEstadoCuenta(id, params),
    enabled: id > 0,
    staleTime: 30_000,
    retry: false,
  });
}

export function useClienteEstadoCuentaSimple(id: number, params: QueryEstadoCuentaSimpleDto) {
  return useQuery({
    // SAFETY: Query DTOs are JSON-compatible records; the key helper only needs this structural view.
    queryKey: clientesKeys.estadoCuentaSimple(id, params as unknown as Record<string, unknown>),
    queryFn: () => svc.getClienteEstadoCuentaSimple(id, params),
    enabled: id > 0,
    staleTime: 30_000,
    retry: false,
  });
}

export interface ClienteEstadoCuentaExportVariables {
  format: EstadoCuentaExportFormat;
  filters: EstadoCuentaExportFilters;
}

export function useExportClienteEstadoCuenta(id: number) {
  return useMutation({
    mutationFn: ({ format, filters }: ClienteEstadoCuentaExportVariables) =>
      svc.exportClienteEstadoCuenta(id, format, filters),
  });
}

export interface ClienteEstadoCuentaSimpleExportVariables {
  format: EstadoCuentaExportFormat;
  filters: EstadoCuentaSimpleExportFilters;
}

export function useExportClienteEstadoCuentaSimple(id: number) {
  return useMutation({
    mutationFn: ({ format, filters }: ClienteEstadoCuentaSimpleExportVariables) =>
      svc.exportClienteEstadoCuentaSimple(id, format, filters),
  });
}

interface UseClienteSerieMensualOptions {
  enabled?: boolean;
}

export function useClienteSerieMensual(
  id: number,
  params: QueryEstadoCuentaSerieMensualDto,
  options?: UseClienteSerieMensualOptions,
) {
  return useQuery({
    // SAFETY: Query DTOs are JSON-compatible records; the key helper only needs this structural view.
    queryKey: clientesKeys.serieMensual(id, params as unknown as Record<string, unknown>),
    queryFn: () => svc.getClienteSerieMensual(id, params),
    enabled: id > 0 && (options?.enabled ?? true),
    staleTime: 30_000,
    retry: false,
  });
}
